import { TransactionActionHistory } from './../transaction-reports';
import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { takeUntil } from 'rxjs/operators';
import { Moment } from 'moment';
import * as moment from 'moment';
import * as momentTz from 'moment-timezone';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { AlertService, GatewayTransactionReportService, LoaderService, StorageService } from '../../../_services';
import { Form } from '../../../_forms/form';
import { Pager, User } from '../../../_models';
import { config } from '../../../../config/config';
import { isEmpty } from 'lodash';
import {
  TransactionDetailFields,
  TransactionDetail,
  CardType,
  CardIconColorMap,
  getTxnAmount,
} from '../transaction-reports';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { formatMoney } from '../../../_helpers/functions';

@Component({
  moduleId: module.id.toString(),
  selector: 'gateway-transaction-detail',
  templateUrl: './gateway-transaction-detail.component.html',
  styleUrls: ['./gateway-transaction-detail.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state(
        'collapsed',
        style({
          height: '0px',
          opacity: 0,
        })
      ),
      state(
        'expanded',
        style({
          height: '*',
          opacity: 1,
        })
      ),
      transition('collapsed <=> expanded', [animate('300ms ease-in-out')]),
    ]),
  ],
})
export class GatewayTransactionDetailComponent extends Form implements OnInit {
  currentUser: User;
  date: { startDate: Moment; endDate: Moment };
  dateFromParams = '';
  txnId = null;
  cardType = CardType;
  cardIconColorMap = CardIconColorMap;
  isPDFLoading: boolean = false;
  isPrintLoading: boolean = false;
  transaction: TransactionDetail = null;
  user: User;

  constructor(
    protected alertService: AlertService,
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected formBuilder: FormBuilder,
    public reportsService: GatewayTransactionReportService,
    protected storageService: StorageService,
    protected loader: LoaderService
  ) {
    super(alertService, router, location);
  }

  ngOnInit() {
    this.user = this.storageService.get('currentUser');
    this.route.params.subscribe((params) => {
      this.txnId = params['id'];
      this.dateFromParams = params['date'];
    });

    this.loader.show();

    this.initializeDate();
    this.setCurrentUser();
    this.getReport();
  }

  getReport() {
    this.reportsService
      .getFilteredReport({
        ...this.getReportParameters(),
        page: 1,
        page_size: config.defaultPageSize,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data: Pager) => {
          if (isEmpty(data.results)) {
            this.transaction = null;
          } else {
            this.transaction = data.results[0];

            if (this.transaction.action_history && this.transaction.action_history.length > 0) {
              this.transaction.action_history.forEach((action) => {
                action.showDetails = false;
              });
            }
          }
          this.loader.hide();
        },
        (error) => {
          this.handleError(error);
          this.loader.hide();
        }
      );
  }

  private buildPDF(contentId: string): Promise<jsPDF | null> {
    const element = document.getElementById(contentId);
    const promise = new Promise<jsPDF | null>((resolve) => {
      if (element) {
        // Page dimensions in inches (letter size)
        const pageWidthIn = 8.5;
        const pageHeightIn = 11;
        const marginXIn = 0.4;
        const marginYIn = 0.55;
        const pageNumberHeightIn = 0.3;
        const firstContentYIn = marginYIn + 0.25;

        // Measure where the Actions section header starts AFTER hiding the row so layout matches capture
        let actionsBreakCanvasPx = 0;
        const allHeaders = element.querySelectorAll('.details-header');
        if (allHeaders.length > 0) {
          const actionsHeader = allHeaders[allHeaders.length - 1] as HTMLElement;
          const elementRect = element.getBoundingClientRect();
          const headerRect = actionsHeader.getBoundingClientRect();
          actionsBreakCanvasPx = Math.floor((headerRect.top - elementRect.top) * 5);
        }

        // Declare logo here so doCapture closes over it; load AFTER setting onload to guarantee render
        const imgLogo = new Image();
        const doCapture = () => {
          html2canvas(element, { scale: 5, backgroundColor: '#F8F9FB' }).then((canvas) => {
          const pdf = new jsPDF({ unit: 'in', format: [pageWidthIn, pageHeightIn], compress: true });

          const imgWidthIn = pageWidthIn - marginXIn * 2;
          const pxPerInch = canvas.width / imgWidthIn;

          const firstSlicePx = Math.floor((pageHeightIn - firstContentYIn - pageNumberHeightIn - 0.1) * pxPerInch);
          const regularSlicePx = Math.floor((pageHeightIn - marginYIn - pageNumberHeightIn - 0.1) * pxPerInch);

          const pageSlices: Array<{ start: number; end: number; isFirst: boolean }> = [];
          let currentStart = 0;
          let isFirstSlice = true;

          while (currentStart < canvas.height) {
            const slicePx = isFirstSlice ? firstSlicePx : regularSlicePx;
            let candidateEnd = currentStart + slicePx;
            const minSlicePx = Math.floor(1.0 * pxPerInch);
            let extendedPrevious = false;

            if (actionsBreakCanvasPx > currentStart && actionsBreakCanvasPx < candidateEnd) {
              if (actionsBreakCanvasPx - currentStart < minSlicePx && pageSlices.length > 0) {
                // Break point is too close to the current page start — would create a nearly blank
                // page. Instead, extend the previous slice to actionsBreakCanvasPx so that
                // everything before Actions lands on the previous page, and Actions starts cleanly
                // on the next page with no blank page in between.
                pageSlices[pageSlices.length - 1].end = actionsBreakCanvasPx;
                currentStart = actionsBreakCanvasPx;
                extendedPrevious = true;
              } else {
                candidateEnd = actionsBreakCanvasPx;
              }
            }

            if (!extendedPrevious) {
              candidateEnd = Math.min(candidateEnd, canvas.height);
              pageSlices.push({ start: currentStart, end: candidateEnd, isFirst: isFirstSlice });
              currentStart = candidateEnd;
              isFirstSlice = false;
            }
          }

          // Compute footer icon once outside the loop to avoid repeated canvas allocations
          let sharedFooterIconWidthIn = 0;
          let sharedFooterIconHeightIn = 0;
          let sharedFooterIconDataUrl = '';
          if (imgLogo.naturalWidth > 0) {
            const footerIconHeightIn = 0.18;
            const naturalLogoWidthIn = imgLogo.naturalWidth / 96;
            const naturalLogoHeightIn = imgLogo.naturalHeight / 96;
            const scale = footerIconHeightIn / naturalLogoHeightIn;
            sharedFooterIconHeightIn = footerIconHeightIn;
            sharedFooterIconWidthIn = naturalLogoWidthIn * scale;
            // Render icon at its display size (96 DPI × 2 for crispness) rather than natural size
            // to avoid encoding a large PNG for an icon that renders at a fraction of the size
            const logoRenderPx = Math.round(sharedFooterIconWidthIn * 96 * 2);
            const logoRenderPxH = Math.round(sharedFooterIconHeightIn * 96 * 2);
            const logoCanvas = document.createElement('canvas');
            logoCanvas.width = logoRenderPx;
            logoCanvas.height = logoRenderPxH;
            const logoCtx = logoCanvas.getContext('2d');
            logoCtx.fillStyle = '#F8F9FB';
            logoCtx.fillRect(0, 0, logoRenderPx, logoRenderPxH);
            logoCtx.drawImage(imgLogo, 0, 0, logoRenderPx, logoRenderPxH);
            sharedFooterIconDataUrl = logoCanvas.toDataURL('image/jpeg', 0.9);
          }

          const merchantId = this.transaction ? this.transaction.merchant_id : '';

          pageSlices.forEach((slice, index) => {
            if (index > 0) {
              pdf.addPage();
            }

            pdf.setFillColor(248, 249, 251);
            pdf.rect(0, 0, pageWidthIn, pageHeightIn, 'F');

            const headerTopPaddingIn = 0.3;
            const headerCenterY = headerTopPaddingIn + (marginYIn - headerTopPaddingIn) / 2;

            if (slice.isFirst) {
              // Header row: TXN # (left) + Merchant ID (right), both vertically centered in header area
              // TXN # — baseline offset ~half the font cap height (15pt = 15/72 in, cap ~0.7x)
              const txnId = this.transaction ? this.transaction.transaction_id : '';
              pdf.setTextColor(22, 53, 73);
              pdf.setFontSize(15);
              pdf.setFont('helvetica', 'bold');
              pdf.text(`TXN #${txnId}`, marginXIn, headerCenterY + (15 / 72) * 0.35);
              pdf.setFont('helvetica', 'normal');

              // Merchant ID on the right — vertically centered in header, page 1 only
              pdf.setTextColor(22, 53, 73);
              pdf.setFontSize(15);
              pdf.setFont('helvetica', 'bold');
              pdf.text(`Merchant ID: ${merchantId || '--'}`, pageWidthIn - marginXIn, headerCenterY + (15 / 72) * 0.35, { align: 'right' });
              pdf.setFont('helvetica', 'normal');
            }

            const sliceH = slice.end - slice.start;
            const pageCanvas = document.createElement('canvas');
            pageCanvas.width = canvas.width;
            pageCanvas.height = sliceH;

            const ctx = pageCanvas.getContext('2d');
            ctx.drawImage(canvas, 0, slice.start, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

            const sliceImgHeightIn = sliceH / pxPerInch;
            const imgYIn = slice.isFirst ? firstContentYIn : marginYIn + 0.3; // add some extra padding for subsequent pages to separate from header

            pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.82), 'JPEG', marginXIn, imgYIn, imgWidthIn, sliceImgHeightIn);

            // Footer icon centered at the bottom of every page
            if (sharedFooterIconDataUrl) {
              const footerIconY = pageHeightIn - 0.15 - sharedFooterIconHeightIn / 2 - 0.05;
              pdf.addImage(
                sharedFooterIconDataUrl,
                'JPEG',
                pageWidthIn / 2 - sharedFooterIconWidthIn / 2,
                footerIconY,
                sharedFooterIconWidthIn,
                sharedFooterIconHeightIn
              );
            }

            // Page number bottom-right
            const pageNumberY = pageHeightIn - 0.15;
            pdf.setFontSize(9);
            pdf.setTextColor(100, 116, 139);
            pdf.text(`Page ${index + 1}`, pageWidthIn - marginXIn, pageNumberY, { align: 'right' });
          });

          resolve(pdf);
          });
        };
        imgLogo.src = '/assets/IconsLibrary/solvpath-footer-icon.svg';
        if (imgLogo.complete) {
          doCapture();
        } else {
          imgLogo.onload = doCapture;
          imgLogo.onerror = doCapture;
        }
      } else {
        resolve(null);
      }
    });
    return promise;
  }

  downloadPDF(contentId: string): void {
    this.isPDFLoading = true;
    this.buildPDF(contentId).then((pdf) => {
      if (pdf) {
        pdf.save(`${contentId}.pdf`);
      }
      this.isPDFLoading = false;
    });
  }

  navigateBackToSummary() {
    this.router.navigate(['/checkouts/transaction-summary']);
  }

  initializeDate() {
    if (this.dateFromParams) {
      const txnDate = moment(this.dateFromParams);
      if (txnDate.isValid()) {
        this.date = {
          startDate: txnDate.clone().subtract(1, 'day').startOf('day'),
          endDate: txnDate.clone().add(1, 'day').endOf('day'),
        };
      }
    }
  }

  getTimezone(): string {
    return this.storageService.get('timzone') || momentTz.tz.guess();
  }

  getReportParameters() {
    if (!this.date.startDate || !this.date.endDate) {
      this.initializeDate();
    }

    return {
      accounts: [this.currentUser.account.id],
      customer_mode: false,
      date_filter_type: 'sessions',
      date_range: {
        startDate: moment(this.date.startDate).utc().format('YYYY-MM-DD HH:mm:ss'),
        endDate: moment(this.date.endDate).utc().format('YYYY-MM-DD HH:mm:ss'),
      },
      fields: TransactionDetailFields,
      filters: { transaction_id: this.txnId },
      timezone: this.getTimezone(),
    };
  }

  toggleDetails(selectedAction: any): void {
    this.transaction.action_history.forEach((action) => {
      if (action !== selectedAction) {
        action.showDetails = false; // collapse others
      }
    });
    selectedAction.showDetails = !selectedAction.showDetails; // toggle current one
  }

  getDetailRows(action: TransactionActionHistory) {
    return [
      { label: 'Username', value: `${this.transaction.customer_first_name} ${this.transaction.customer_last_name}` },
      { label: 'IP', value: this.transaction.ip_address || '--' },
      { label: 'Processor Response Code', value: this.transaction.response_code || '--' },
      { label: 'Processor Response Text', value: this.transaction.processor_response_text || '--' },
      { label: 'Gateway Response Text', value: this.transaction.gateway_response_text || '--' },
    ];
  }

  setCurrentUser() {
    this.currentUser = this.storageService.get('currentUser');
  }

  formatTxnDate(attemptedDate: string): string {
    return moment(attemptedDate).format('MMM DD, YYYY');
  }

  formatTxnTime(attemptedDate: string): string {
    return moment(attemptedDate).format('hh:mm A');
  }

  getMaskedCardNumber(cardLastFour: string | null | undefined): string {
    return cardLastFour ? `******${cardLastFour}` : '--';
  }

  getMaskedInstrumentId(instrumentId: string | null | undefined): string {
    let result = '--';
    if (instrumentId && instrumentId.length >= 4) {
      result = `****${instrumentId.slice(-4)}`;
    }
    return result;
  }

  getAmount(amount: string) {
    return '$' + formatMoney(amount);
  }

  getActionAmount(amount: string) {
    return getTxnAmount(amount, this.transaction.transaction_type);
  }

  printReceipt(): void {
    this.isPrintLoading = true;
    this.buildPDF('txn-details').then((pdf) => {
      if (pdf) {
        pdf.autoPrint();
        window.open(pdf.output('bloburl') as unknown as string);
      }
      this.isPrintLoading = false;
    });
  }
}
